import React, { useState, useEffect } from 'react';
import { ViewMode } from '../types/inventory';
import { viewModeToConfig, getFeatures, getThumbnailSize, sizeToLayout } from '../types/viewComposition';
import { ItemGrid } from './inventory/ItemGrid';
import { ItemCard, SavedItem } from './inventory/ItemCard';
import { SwipeableItemCard, SwipeAction } from './inventory/SwipeableItemCard';
import ImageAttributionModal from './ImageAttributionModal';
import { API_ENDPOINTS } from '../config';

interface InventoryContentProps {
  items: SavedItem[];
  viewMode: ViewMode;
  token?: string | null;
  selectedIds: Set<number>;
  isSelectMode?: boolean;
  isEditMode?: boolean;
  user?: any;
  
  // Callbacks
  onItemClick?: (item: SavedItem) => void;
  onMenuClick?: (item: SavedItem) => void; // 3-dot menu (replaces long press)
  onPhotoUpload?: (itemId: number, file: File) => Promise<void>;
  onOpenImageSelection?: (itemId: number) => void;
  onCheckboxToggle?: (item: SavedItem, e: React.MouseEvent) => void;
  onQuantityChange?: (itemId: number, newQuantity: number) => void;
  renderItemOverlay?: (item: SavedItem) => React.ReactNode;
  showQuantityInName?: boolean; // For shop mode: show quantity next to name instead of controls
  highlightZeroQuantity?: boolean; // Show orange color for zero quantity items
  fadingOutItems?: Set<number>; // Items that are fading out after being checked
  isGuestToken?: boolean; // For guest views: use guest API endpoints
  currentUserId?: number; // Current user ID for determining ownership
  isSharedCollection?: boolean; // True if viewing a shared collection
  
  // Loading and error states
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  pendingQuantities?: Record<number, number>;
  onShareBadgeClick?: (item: SavedItem) => void;
  onItemsChange?: (items: SavedItem[]) => void;
  collectionId?: number;
  
  // Swipe actions
  leftSwipeAction?: SwipeAction;
  rightSwipeAction?: SwipeAction;
  swipeEnabled?: boolean;
  onSwipeAction?: (item: any, action: SwipeAction) => void;
  useQuantityBadge?: boolean;
  onQuantityBadgeClick?: (item: any) => void;
  disableNameEdit?: boolean; // Disable name click to edit (e.g., in Shop mode)
  bindleItemIds?: Set<number>; // Items that are in the active bindle (show circle in checkbox)
  
  // Empty state context
  hasActiveFilter?: boolean; // True if search/filter is active but returned no results
  totalItemsBeforeFilter?: number; // Total items before filtering
}

export interface InventoryContentExtraProps {
  thumbnailSize?: 'small' | 'medium' | 'large' | 'x-large';
}

export const InventoryContent: React.FC<InventoryContentProps & InventoryContentExtraProps> = ({
  items,
  viewMode,
  token,
  currentUserId,
  isSharedCollection,
  selectedIds,
  isSelectMode,
  isEditMode,
  user,
  onItemClick,
  onMenuClick,
  onPhotoUpload,
  onOpenImageSelection,
  onCheckboxToggle,
  onQuantityChange,
  renderItemOverlay,
  showQuantityInName,
  highlightZeroQuantity,
  fadingOutItems,
  isGuestToken,
  thumbnailSize = 'medium',
  leftSwipeAction,
  rightSwipeAction,
  swipeEnabled = false,
  onSwipeAction,
  useQuantityBadge,
  onQuantityBadgeClick,
  disableNameEdit = false,
  bindleItemIds,
  hasActiveFilter,
  totalItemsBeforeFilter,
  loading,
}) => {
  const [imageModalItem, setImageModalItem] = useState<SavedItem | null>(null);

  // Convert old ViewMode to new ViewConfig
  const viewConfig = viewModeToConfig(viewMode);
  const { collectionType } = viewConfig;
  
  // Size determines everything: layout AND what details to show
  const layout = sizeToLayout(thumbnailSize);
  let features = getFeatures(collectionType, thumbnailSize);
  
  // Override features for shop mode: hide quantity controls, show quantity in name
  if (showQuantityInName) {
    features = { ...features, showQuantity: false };
  }
  
  const size = thumbnailSize;

  // Loading state - only show if there are no items to display
  // This prevents clearing existing items while fetching updates
  if (loading && items.length === 0) {
    return (
      <div className="empty-state" style={{ paddingTop: '40px', paddingBottom: '160px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--color-text-secondary)', margin: '0 0 8px 0' }}>
          Loading...
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: 0, maxWidth: '280px', lineHeight: 1.5 }}>
          Fetching your items
        </p>
      </div>
    );
  }

  // Empty state
  if (items.length === 0) {
    // Determine if this is "no matches" vs "empty collection"
    const isFilteredEmpty = hasActiveFilter && totalItemsBeforeFilter && totalItemsBeforeFilter > 0;
    
    return (
      <div className="empty-state" style={{ paddingTop: '40px', paddingBottom: '160px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--color-text-secondary)', margin: '0 0 8px 0' }}>
          {isFilteredEmpty ? 'No matching items' : 'No items yet'}
        </h3>
        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', margin: 0, maxWidth: '280px', lineHeight: 1.5 }}>
          {isFilteredEmpty ? 'Try adjusting your search or filters' : 'Add your first item to get started'}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Single unified render path */}
      <ItemGrid layout={layout} size={size}>
        {items.map((item) => {
          const isSelected = selectedIds.has(item.id);
          const isReadOnly = item.my_role === 'viewer' || disableNameEdit;
          const isNonSelectable = isEditMode && item.my_role === 'viewer';
          // Hide lock icon when disabled for UX (Shop mode), but show it for permission-based readonly
          const showLock = item.my_role === 'viewer';

          // Use SwipeableItemCard when swipe is enabled, otherwise regular ItemCard
          if (swipeEnabled && (leftSwipeAction || rightSwipeAction)) {
            return (
              <SwipeableItemCard
                key={item.id}
                item={item}
                size={size}
                features={features}
                isSelected={isSelected}
                isSelectMode={isSelectMode}
                token={token}
                onItemClick={onItemClick}
                onImageClick={isSelectMode ? undefined : (item) => setImageModalItem(item)}
                onMenuClick={onMenuClick}
                onCheckboxToggle={onCheckboxToggle}
                onQuantityChange={onQuantityChange}
                onPhotoUpload={onPhotoUpload}
                onOpenImageSelection={onOpenImageSelection}
                renderOverlay={renderItemOverlay}
                showQuantityInName={showQuantityInName}
                highlightZeroQuantity={highlightZeroQuantity}
                hideMenu={showQuantityInName}
                isFadingOut={fadingOutItems?.has(item.id)}
                isGuestToken={isGuestToken}
                currentUserId={currentUserId}
                isSharedCollection={isSharedCollection}
                isReadOnly={isReadOnly}
                showLockIcon={showLock}
                leftSwipeAction={leftSwipeAction}
                rightSwipeAction={rightSwipeAction}
                swipeEnabled={swipeEnabled}
                onSwipeAction={onSwipeAction}
                useQuantityBadge={useQuantityBadge}
                onQuantityBadgeClick={onQuantityBadgeClick}
                isInBindle={bindleItemIds?.has(item.id)}
              />
            );
          }

          return (
            <ItemCard
              key={item.id}
              item={item}
              size={size}
              features={features}
              isSelected={isSelected}
              isSelectMode={isSelectMode}
              token={token}
              onItemClick={onItemClick}
              onImageClick={isSelectMode ? undefined : (item) => setImageModalItem(item)}
              onMenuClick={onMenuClick}
              onCheckboxToggle={onCheckboxToggle}
              onQuantityChange={onQuantityChange}
              onPhotoUpload={onPhotoUpload}
              onOpenImageSelection={onOpenImageSelection}
              renderOverlay={renderItemOverlay}
              showQuantityInName={showQuantityInName}
              highlightZeroQuantity={highlightZeroQuantity}
              hideMenu={showQuantityInName}
              isFadingOut={fadingOutItems?.has(item.id)}
              isGuestToken={isGuestToken}
              currentUserId={currentUserId}
              isSharedCollection={isSharedCollection}
              isReadOnly={isReadOnly}
              showLockIcon={showLock}
              isInBindle={bindleItemIds?.has(item.id)}
            />
          );
        })}
      </ItemGrid>

      {/* Single modal rendering - no duplication! */}
      {imageModalItem && (imageModalItem.image_url || imageModalItem.image_id) && (
        <ImageAttributionModal
          isOpen={true}
          onClose={() => setImageModalItem(null)}
          imageUrl={
            isGuestToken 
              ? `/api/collections/guest/${token}/images/${imageModalItem.image_id}`
              : (imageModalItem.image_id ? `${API_ENDPOINTS.GET_IMAGE_CONTENT(imageModalItem.image_id)}` : imageModalItem.image_url || '')
          }
          itemName={imageModalItem.name}
          token={token || undefined}
          photographerName={(imageModalItem as any).photographer_name}
          photographerUsername={(imageModalItem as any).photographer_username}
          unsplashUrl={(imageModalItem as any).unsplash_url}
          onChangeImage={onOpenImageSelection && (
            imageModalItem.access_type === 'my_personal' || 
            imageModalItem.my_role !== 'viewer'
          ) ? () => {
            setImageModalItem(null);
            onOpenImageSelection(imageModalItem.id);
          } : undefined}
          isGuestToken={isGuestToken}
        />
      )}
    </>
  );
};
